import { Inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';

import { isEqual } from 'lodash';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  first,
  map,
  publishReplay,
  refCount,
  tap
} from 'rxjs/operators';

import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@ipreo/northstar';

import { SessionFacade } from './session.facade';
import { Session } from '../types/session.types';
import { Permissions, UserPermission } from '../types/user-permissions.types';
import { selectSharedState } from '../state/reducer';
import { LoadUserPermissions } from '../state/user-permissions/actions';
import { UserPermissionsState } from '../state/user-permissions/state';
import { PlatformType } from '../types/muni-environment';

@Injectable()
export class PermissionsFacade {
  public readonly permissions$: Observable<Permissions>;

  private platform: PlatformType;

  private sellsideMMD = new BehaviorSubject(true);

  constructor(
    private store: Store<unknown>,
    private sessionFacade: SessionFacade,
    @Inject(ENVIRONMENT_CONFIG) config: EnvironmentConfig
  ) {
    this.platform = config.params.platform as PlatformType;

    if (this.platform === PlatformType.buyside) {
      this.permissions$ = store.pipe(
        select(selectSharedState),
        filter((state) => Boolean(state)),
        map(({ userPermissions }) => {
          if (userPermissions.error) {
            throw new Error(userPermissions.error);
          } else {
            return userPermissions;
          }
        }),
        tap((settingsState) => this.loadBuysidePermissions(settingsState)),
        filter((permissionsState) => Boolean(permissionsState.userPermissions)),
        tap(({ userPermissions: { ipreoAccountUserId } }) => {
          this.sessionFacade.setBuysideSub(ipreoAccountUserId);
        }),
        map(
          ({ userPermissions: { permissions } }): Permissions => ({
            mmd: permissions.includes(UserPermission.MunicipalMmdData),
            competitiveBids: permissions.includes(
              UserPermission.MuniPermissionedUser
            ),
            createBid: false,
            following: true,
            groupFollowing: permissions.includes(
              UserPermission.MunicipalGroupFollow
            ),
            assignments: permissions.includes(UserPermission.MuniAssignments),
            commenting: permissions.includes(
              UserPermission.MunicipalCommenting
            ),
            ownership: false,
            marketDrawer: false,
            versionSelector: false,
            calendarPricing: true,
            internalTracking: true,
            internalStatusInterested: permissions.includes(
              UserPermission.MunicipalInternalStatusInterested
            ),
            internalStatusNotApproved: permissions.includes(
              UserPermission.MunicipalInternalStatusNotApproved
            ),
            internalStatusInProgress: permissions.includes(
              UserPermission.MunicipalInternalStatusInProgress
            ),
            internalStatusApproved: permissions.includes(
              UserPermission.MunicipalInternalStatusApproved
            ),
            isMuniPublicUser: permissions.includes(
              UserPermission.MuniPublicUser
            ),
            isMuniPermissionedUser: permissions.includes(
              UserPermission.MuniPermissionedUser
            ),
            isDesignationsEnabled: permissions.includes(
              UserPermission.Designations
            )
          })
        ),
        distinctUntilChanged(isEqual),
        publishReplay(1),
        refCount()
      ) as Observable<Permissions>;
    } else {
      this.permissions$ = combineLatest(
        this.sessionFacade.session$,
        this.sellsideMMD
      ).pipe(
        map(([session, mmd]) => ({
          mmd,
          competitiveBids: false,
          createBid: true,
          following: false,
          groupFollowing: false,
          assignments: false,
          commenting: false,
          ownership: true,
          marketDrawer: session.deptType !== Session.DeptType.SyndDesk,
          versionSelector: true,
          calendarPricing: true,
          internalTracking: false,
          internalStatusInterested: false,
          internalStatusNotApproved: false,
          internalStatusInProgress: false,
          internalStatusApproved: false,
          isMuniPublicUser: false,
          isMuniPermissionedUser: true
        })),
        distinctUntilChanged(isEqual),
        publishReplay(1),
        refCount()
      );
    }
  }

  public withPermission(permission: keyof Permissions) {
    return this.permissions$.pipe(
      first(),
      filter((permissions) => permissions[permission]),
      map((permissions) => permissions[permission])
    );
  }
  public getPermission(permission: keyof Permissions) {
    return this.permissions$.pipe(
      map((permissions) => permissions[permission])
    );
  }

  public setMmdAccess(value: boolean) {
    this.sellsideMMD.next(value);
  }

  private loadBuysidePermissions(state: UserPermissionsState) {
    if (!state.error && !state.loading && !state.loaded) {
      setTimeout(() => {
        this.store.dispatch(new LoadUserPermissions());
      }, 1);
    }
  }
}
